// Promise wrappers over the few IndexedDB calls the corpus makes. The atomic
// page commit in corpus.js deliberately stays in callback style inside its
// transaction; these cover everything around it.

export const isQuotaError = error =>
  !!error && (error.name === 'QuotaExceededError' || error.code === 22);

export const storageUnavailable = () =>
  Object.assign(new Error('IndexedDB is not available'), { name: 'StorageUnavailableError' });

export const requestToPromise = request =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB request failed'));
  });

/** Resolves when the transaction commits; rejects with its error when it aborts. */
export const transactionDone = tx =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () =>
      reject(tx.error || Object.assign(new Error('aborted'), { name: 'AbortError' }));
  });

export function openDatabase(indexedDB, name, version, upgrade) {
  return new Promise((resolve, reject) => {
    let request;
    try {
      request = indexedDB.open(name, version);
    } catch (error) {
      reject(error);
      return;
    }
    request.onupgradeneeded = event => upgrade(request.result, event);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error(`open ${name} failed`));
  });
}

export function deleteDatabase(indexedDB, name) {
  return new Promise((resolve, reject) => {
    let request;
    try {
      request = indexedDB.deleteDatabase(name);
    } catch (error) {
      reject(error);
      return;
    }
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error || new Error(`delete ${name} failed`));
  });
}

/** Database names, or null where the engine cannot list them (older WebKit, Firefox before 126). */
export async function listDatabases(indexedDB) {
  if (typeof indexedDB?.databases !== 'function') return null;
  try {
    const list = await indexedDB.databases();
    return (list || []).map(d => d?.name).filter(Boolean);
  } catch (e) {
    return null;
  }
}

export function readOne(db, storeName, key) {
  const tx = db.transaction(storeName, 'readonly');
  return requestToPromise(tx.objectStore(storeName).get(key));
}

export function readAll(db, storeName) {
  const tx = db.transaction(storeName, 'readonly');
  return requestToPromise(tx.objectStore(storeName).getAll());
}
